import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  effect,
} from '@angular/core';
import * as moment from 'moment';
import { CalendarEvent } from 'src/app/interfaces/interfaces';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';

import { BookingsStore } from 'src/app/stores/bookings.store';
import { SwiperContainer } from 'swiper/element';

@Component({
  selector: 'app-swiper-calendar',
  templateUrl: './swiper-calendar.component.html',
  styleUrls: ['./swiper-calendar.component.scss'],
})
export class SwiperCalendarComponent implements OnInit, OnChanges {
  slides = [];
  daysNames = [];
  locale = this.dateTimeService.selectedDateTimeFormat;
  /** First auto-selected day after load / segment change */
  didHighlightDateOnFirstRender = false;
  @ViewChild('swiper') swiperRef: ElementRef<SwiperContainer>;
  @Output() scrollToSection = new EventEmitter<any>();
  @Input() selectedSegment: string;

  constructor(
    private dateTimeService: DatetimeFormatService,
    private bookingsStore: BookingsStore
  ) {
    effect(() => {
      this.bookingsStore.allBookings();
      this.bookingsStore.myBookings();
      this.bookingsStore.myInvitations();
      this.disableSlidesBasedOnMeetings(this.selectedSegment);
    });
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSegment']) {
      this.didHighlightDateOnFirstRender = false;
      this.disableSlidesBasedOnMeetings(this.selectedSegment);
    }
  }

  /** Same events as the bookings list for the active segment */
  private bookingsForSegment(segment: string): CalendarEvent[] {
    if (segment === 'mine') {
      return Object.values(this.bookingsStore.myBookings()).flat();
    }
    if (segment === 'invitations') {
      return Object.values(this.bookingsStore.myInvitations()).flat();
    }
    return this.bookingsStore.allBookings();
  }

  generateSlides() {
    this.slides = [];
    this.daysNames = [];
    const today = new Date();

    const closestMonday = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days, otherwise go to previous Monday
    closestMonday.setDate(today.getDate() + diff);

    for (let i = 0; i < 35; i++) { // Generate 5 weeks (35 days) of slides
      const currentDate = new Date(closestMonday);
      currentDate.setDate(closestMonday.getDate() + i);

      const dayName = currentDate.toLocaleDateString(this.locale(), {
        weekday: 'short',
      });
      if (this.daysNames.length < 7) {
        this.daysNames.push(dayName);
      }
      const dayNumber = currentDate.getDate();
      const monthNumber = currentDate.getMonth();
      const href = moment(currentDate).format('YYYY-MM-DD');
      const monthName = currentDate.toLocaleString(this.locale(), {
        month: 'short',
      });
      this.slides.push({
        dayName: dayName,
        monthName: monthName,
        dayNumber: dayNumber,
        monthNumber: monthNumber,
        isSelected: false,
        isSemiSelected:
          today.getDate() == dayNumber && today.getMonth() == monthNumber,
        href: href,
        hasMeetings: false,
        hasInvitations: false,
        hasMeetingsOrInvitations: false,
      });
    }
  }

  disableSlidesBasedOnMeetings(selectedSegment: string) {
    const bookings = this.bookingsForSegment(selectedSegment);

    this.generateSlides();

    if (selectedSegment === 'dailyCalendar') {
      const todaySlide = this.slides.find(
        (slide) => slide.href === moment().format('YYYY-MM-DD')
      );
      if (todaySlide) {
        todaySlide.hasMeetingsOrInvitations = true;
      }
    }

    bookings.forEach((booking) => {
      const dateKey = moment(new Date(booking.startTime)).format('YYYY-MM-DD');
      const slideIndex = this.slides.findIndex((slide) => slide.href === dateKey);
      if (slideIndex === -1) {
        return;
      }

      if (selectedSegment === 'mine') {
        this.slides[slideIndex].hasMeetings = true;
      } else if (selectedSegment === 'invitations') {
        this.slides[slideIndex].hasInvitations = true;
      } else if (selectedSegment === 'dailyCalendar') {
        this.slides[slideIndex].hasMeetingsOrInvitations = true;
      }
    });

    if (!this.didHighlightDateOnFirstRender) {
      let firstIndex = -1;
      if (selectedSegment === 'mine') {
        firstIndex = this.slides.findIndex((slide) => slide.hasMeetings);
      } else if (selectedSegment === 'invitations') {
        firstIndex = this.slides.findIndex((slide) => slide.hasInvitations);
      } else if (selectedSegment === 'dailyCalendar') {
        firstIndex = this.slides.findIndex(
          (slide) => slide.hasMeetingsOrInvitations
        );
      }

      if (firstIndex !== -1) {
        this.slides[firstIndex].isSelected = true;
        this.didHighlightDateOnFirstRender = true;
      }
    }
  }

  highlightDate(dateId: string, shouldAnimate = false) {
    const index = this.slides.findIndex((slide) => slide.href === dateId);
    if (index !== -1) {
      this.slides.forEach((slide) => (slide.isSelected = false));
      this.slides[index].isSelected = true;

      if (
        this.swiperRef?.nativeElement?.swiper
      ) {
        const swiper = this.swiperRef.nativeElement.swiper;

        const startIndex = swiper.activeIndex;
        const endIndex = startIndex + +swiper.params?.slidesPerView - 1;

        if (index < startIndex || index > endIndex) {
          let targetSlide = Math.floor(index / 7) * 7;

          targetSlide = Math.max(
            0,
            Math.min(
              targetSlide,
              this.slides.length - +swiper.params.slidesPerView
            )
          );

          swiper.slideTo(targetSlide, shouldAnimate ? 250 : 0, false);
        }
      }
    }
  }

  chooseDate(date) {
    this.scrollToSection.emit(date);
    setTimeout(() => {
      this.slides.forEach((slide) => (slide.isSelected = false));
      date.isSelected = true;
    }, 255);
  }
}
